import { useState, useEffect } from 'react';
import { C, FONT, Card, SectionHeader, pct, TimeframeControlWithDates, useTimeframe, rangeLabel, DEFAULT_MARKER_NAME } from './adminShared';
import { getEmailStats } from '../../utils/api';
import { useSequenceEmails, storedEmailCopy, NO_DESCRIPTION } from './sequenceEmails';

// ── STRUCTURE ONLY: which emails, in which group, in what order ────────────
// Each email's NAME and TRIGGER DESCRIPTION are not written here. They live in
// ry_sequence_emails (edited in Booking Hub's Email > Funnel tab) and arrive
// through GET /api/admin/sequence-emails — see ./sequenceEmails.js. An email
// with nothing stored shows "No description set", never wording from code.
//
// seqKey maps DIRECTLY to the `sequence` field returned by
// /api/admin/email-stats — that endpoint no longer combines anything, so
// every key here (including each Seq 2 / Seq 8 child) must match a raw
// email_events.sequence value exactly, one row per key.
//
// THIS LIST IS THE CANONICAL SOURCE OF TRUTH for which rows the tab shows.
// A sequence with zero events still gets a row (see the ?? 0 fallback below)
// — but ONLY if it's listed here. A sequence the backend can send that is
// missing from this list is invisible on the tab even though its data exists
// in the API response (this happened to sequence_7a_reschedule before it was
// added below — 3 real rows in email_events, zero visibility on the tab,
// because nothing here named it). When a new sendXEmail() is added in
// backend/src/services/email.js, add its seqKey here in the same commit.
//
// The group label and sublabel are this tab's own section headings (how the
// funnel is laid out on screen), not an email's name or description.
//
// Seq 2.5 and Seq 2.9 are TWO DIFFERENT EMAILS with two different stored keys:
// cron.js runSequence2_5CartAbandon() writes sequence_2_5 (cart abandonment)
// and runSequence2_9() writes sequence_2_9 (payment recovery). Until 23 Sep
// 2026 one row here read sequence_2_5 under a "payment recovery" label.
//
// Payment Recovery's zeros are the point, not a bug: ry_failed_payments has no
// rows, and its one historic email_events row is Ben's own test send
// (excluded by /email-stats via config/benEmails.js).
//
// NO GROUP TOTAL ROW — see EmailGroupSection below.
export const EMAIL_GROUPS_CONFIG = [
  { label: 'Opt-In', sublabel: 'go.benmilston.com', sequences: [
    { seqKey: 'sequence_1'  },
    { seqKey: 'sequence_1b' },
  ]},
  // One row per email in the chain, oldest first — the point is spotting
  // where people stop opening/clicking as the sequence goes on.
  { label: 'Pre-Purchase Nurture', sublabel: 'Watched masterclass, not yet bought', sequences: [
    { seqKey: 'sequence_2_email_1' },
    { seqKey: 'sequence_2_email_2' },
    { seqKey: 'sequence_2_email_3' },
  ]},
  // Two emails since 2026-10-13: sequence_2_5 (Email A — the single cart email,
  // live 31 Aug – 1 Oct and again from 13 Oct) then sequence_2_5_email_4
  // (Email B). Emails 1–3 of the four-email series (1 – 13 Oct) are retired
  // and stay listed for their history. Every key counts as itself
  // (config/sequenceKeys.js countAsSequence no longer folds anything).
  { label: 'Cart Abandonment', sublabel: "Clicked buy on the sales page, didn't pay", sequences: [
    { seqKey: 'sequence_2_5' },
    { seqKey: 'sequence_2_5_email_1' },
    { seqKey: 'sequence_2_5_email_2' },
    { seqKey: 'sequence_2_5_email_3' },
    { seqKey: 'sequence_2_5_email_4' },
  ]},
  { label: 'Payment Recovery', sublabel: 'Card declined at checkout', sequences: [
    { seqKey: 'sequence_2_9' },
  ]},
  // The call funnel (2026-10-15): Personal Blueprint Session emails, sent only
  // to call-funnel leads and only while the call funnel switch is ON
  // (backend services/callFunnelEmails.js). These keys have no "sequence_"
  // prefix — the email_events sequence is the ry_sequence_emails email_key.
  // Names and descriptions come from those rows, like every other line here.
  { label: 'Call Funnel', sublabel: 'Personal Blueprint Session — call-funnel leads only', sequences: [
    { seqKey: 'call_nurture_email1' },
    { seqKey: 'call_nurture_email2' },
    { seqKey: 'call_nurture_email3' },
    { seqKey: 'call_popup_email1' },
    { seqKey: 'call_popup_email2' },
  ]},
  // The call follow-up (2026-10-15): after a Personal Blueprint Session is
  // marked "Showed — follow up". Any number of emails (call_followup_email1..N,
  // rows added later in Booking Hub's Email → Funnel), so the rows are taken
  // from the stored emails by PREFIX — see EMAIL_GROUPS below. No rows, no group.
  { label: 'Call Follow-up', sublabel: 'After a Personal Blueprint Session marked "Showed — follow up"', dynamicPrefix: 'call_followup_email', sequences: [] },
  { label: 'RY Purchase', sublabel: 'Just bought Recondition Yourself', sequences: [
    { seqKey: 'sequence_3' },
    { seqKey: 'sequence_4' },
  ]},
  { label: 'Inside RY Portal', sublabel: 'Active in the course', sequences: [
    { seqKey: 'sequence_5' },
    { seqKey: 'sequence_6' },
  ]},
  { label: 'Breakthrough Mapping Session', sublabel: 'Booked a call', sequences: [
    { seqKey: 'sequence_7a' },
    { seqKey: 'sequence_7a_reschedule' },
    { seqKey: 'sequence_7b' },
    { seqKey: 'sequence_7c' },
  ]},
  { label: 'UUU Upsell', sublabel: 'Completed course, no UUU purchase', sequences: [
    { seqKey: 'sequence_8_email_1' },
    { seqKey: 'sequence_8_email_2' },
    { seqKey: 'sequence_8_email_3' },
    { seqKey: 'sequence_8_email_4' },
    { seqKey: 'sequence_8_email_5' },
    { seqKey: 'sequence_8_email_6' },
    { seqKey: 'sequence_8_email_7' },
  ]},
];

const COL_WIDTHS = ['28%', '10%', '10%', '12%', '10%', '12%', '8%'];

// NO GROUP TOTAL ROW — deliberate, at Ben's request (15 Aug 2026).
// A sequence stops when an earlier event fires: someone who buys never
// receives the rest of the nurture emails. So the emails in a group are sent
// to DIFFERENT, shrinking populations, and a sum or an average across them
// describes no real audience. Read each sequence on its own line instead.
// The API still returns every per-sequence figure; only the roll-up is gone.
const EmailGroupSection = ({ group, isFirst }) => {
  const hc = '#1A5C2A';
  return (
    <>
      <tr style={{ background: '#EEF5EF', borderTop: isFirst ? 'none' : `2px solid ${C.border}` }}>
        <td colSpan={7} style={{ padding: '13px 16px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 3, height: 30, background: hc, borderRadius: 2, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: hc }}>{group.label}</div>
              <div style={{ fontSize: 11, color: C.accent, marginTop: 1 }}>{group.sublabel}</div>
            </div>
          </div>
        </td>
      </tr>
      {group.sequences.map((s, i) => (
        <tr key={i} style={{ background: i % 2 === 0 ? C.card : '#FDFAF8', borderBottom: `1px solid ${C.border}` }}>
          <td style={{ padding: '10px 16px 10px 28px', color: C.heading, fontWeight: 600, width: COL_WIDTHS[0] }}>
            {s.name ?? <span style={{ color: C.accent, fontWeight: 400 }}>{s.seqKey}</span>}
            <div style={{ fontSize: 10, color: C.accent, fontWeight: 400, marginTop: 2, fontStyle: s.trigger ? 'normal' : 'italic' }}>{s.trigger ?? (s.copyLoading ? 'Loading…' : NO_DESCRIPTION)}</div>
          </td>
          <td style={{ padding: '10px 16px', color: C.text, width: COL_WIDTHS[1] }}>{s.sent.toLocaleString()}</td>
          <td style={{ padding: '10px 16px', color: C.text, width: COL_WIDTHS[2] }}>{s.opened.toLocaleString()}</td>
          <td style={{ padding: '10px 16px', width: COL_WIDTHS[3] }}>
            <span style={{ background: C.border + '80', color: C.heading, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{pct(s.opened, s.sent)}</span>
          </td>
          <td style={{ padding: '10px 16px', color: C.text, width: COL_WIDTHS[4] }}>{s.clicked.toLocaleString()}</td>
          <td style={{ padding: '10px 16px', width: COL_WIDTHS[5] }}>
            <span style={{ background: `${C.cta}25`, color: '#B8860B', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{pct(s.clicked, s.sent)}</span>
          </td>
          <td style={{ padding: '10px 16px', color: s.bounced > 10 ? C.red : C.text, width: COL_WIDTHS[6] }}>{s.bounced}</td>
        </tr>
      ))}
    </>
  );
};

export const AdminEmail = () => {
  // ── THE SAME TIMEFRAME CONTROL AS EVERY OTHER TAB ───────────────────────
  // Same hook, same presets, same from/to — parity across tabs was decided in
  // August and this tab was the one still without it. 'all' resolves to no
  // from/to, so the default request is exactly the one this tab always made.
  // Opens on the saved "Opt-in Copy Change" marker when it exists; the fetch waits for tfReady.
  const { range, setRange, customFrom, setCustomFrom, customTo, setCustomTo, from, to, ready: tfReady } =
    useTimeframe('all', { preferMarkerName: DEFAULT_MARKER_NAME });
  const [statsMap, setStatsMap] = useState({});  // seqKey → { sent, opened, clicked, bounced }
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  // Every email's name + trigger description: stored, never written here.
  const copy = useSequenceEmails();

  useEffect(() => {
    if (!tfReady) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    getEmailStats(from, to)
      .then(res => {
        if (!active) return;
        const map = {};
        (res.data || []).forEach(row => { map[row.sequence] = row; });
        setStatsMap(map);
      })
      .catch(err => { if (active) setError(err.response?.data?.error || err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [from, to, tfReady]);

  // Overlay live stats onto the config structure; fall back to zeros while loading
  // A group with a dynamicPrefix lists every stored email key that starts with
  // it (in number order) plus any key that has sends in this period.
  const prefixKeys = (prefix) => [...new Set([
    ...Object.keys(copy.bySeq || {}),
    ...Object.keys(statsMap),
  ].filter(k => k.startsWith(prefix)))]
    .sort((a, b) => Number(a.slice(prefix.length)) - Number(b.slice(prefix.length)))
    .map(seqKey => ({ seqKey }));
  const EMAIL_GROUPS = EMAIL_GROUPS_CONFIG
    .map(group => (group.dynamicPrefix ? { ...group, sequences: prefixKeys(group.dynamicPrefix) } : group))
    .filter(group => !group.dynamicPrefix || group.sequences.length > 0)
    .map(group => ({
    ...group,
    sequences: group.sequences.map(seq => {
      const live = statsMap[seq.seqKey];
      const stored = storedEmailCopy(copy, seq.seqKey);
      return {
        ...seq,
        name:        stored.name,
        trigger:     stored.trigger,
        copyLoading: !copy.ready,
        sent:    live?.sent    ?? 0,
        opened:  live?.opened  ?? 0,
        clicked: live?.clicked ?? 0,
        bounced: live?.bounced ?? 0,
      };
    }),
  }));

  return (
    <div style={{ fontFamily: FONT, color: C.text }}>
      {/* The period is the SELECTED one, never a hardcoded "All time" — that
          label became a factually wrong number the moment a window could be
          chosen (the Traffic & UTM tab shipped exactly that bug in August). */}
      <SectionHeader
        title="Email Sequence Performance"
        sub={loading ? 'Loading…' : error ? 'Failed to load stats' : `Grouped by funnel stage · Sourced from email_events table · ${rangeLabel(range)}`}
      />
      <TimeframeControlWithDates
        range={range} setRange={setRange}
        customFrom={customFrom} setCustomFrom={setCustomFrom}
        customTo={customTo} setCustomTo={setCustomTo}
      />
      {/* ONE basis, said once. The window is by SEND date: an email counts if it
          was sent in the period, and its opens and clicks count whenever they
          happened — even after the period ended. */}
      <div style={{ fontSize: 11, color: C.accent, marginTop: -8, marginBottom: 16, lineHeight: 1.5 }}>
        Counts the emails sent in this period; their opens and clicks count whenever they happened.
      </div>
      {copy.failed && (
        <div style={{ background: C.redBg, border: '1px solid #F5C6C0', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: C.red }}>
          ✗ Couldn’t load the emails’ stored names and descriptions — each row shows its key and “{NO_DESCRIPTION}”. The figures are unaffected.
        </div>
      )}
      {error && (
        <div style={{ background: C.redBg, border: '1px solid #F5C6C0', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, color: C.red }}>
          ✗ Failed to load email stats: {error}
        </div>
      )}
      <Card style={{ padding: '0 0 20px 0', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${C.border}` }}>
                {['Sequence', 'Sent', 'Opened', 'Open Rate', 'Clicked', 'Click Rate', 'Bounced'].map((h, i) => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: C.heading, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap', minWidth: i === 0 ? 220 : 80 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {EMAIL_GROUPS.map((g, i) => <EmailGroupSection key={i} group={g} isFirst={i === 0} />)}
            </tbody>
          </table>
        </div>
        <div style={{ margin: '14px 24px 0', fontSize: 10, color: C.accent, lineHeight: 1.7, padding: '10px 14px', background: '#FDFAF8', borderRadius: 8 }}>
          ⚠ Open rates may be understated due to Apple Mail and other email clients blocking tracking pixels. Click rates are more reliable.
          {' '}Seq 2 (nurture) and Seq 8 (upsell) are shown one row per email in the chain, oldest first, so you can see where people stop opening or clicking.
        </div>
      </Card>
    </div>
  );
};
